import React, { useState, useEffect } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { Link, useSearchParams } from "react-router-dom";
import { useGetWithdrawsQuery } from "../../../features/withdraws/withdrawSlice";
import { WithdrawsTableSkeleton } from "./WithdrawsTableSkeleton";
import { WithdrawalType } from "./types";
import { CryptoPayoutsTablePanel } from "./CryptoPayoutsTablePanel";

export const WithdrawsTablePanel = () => {
  const [page, setPage] = useState<number>(1);
  const [searchParams] = useSearchParams();
  const pageFromUrl = searchParams.get("page");

  useEffect(() => {
    setPage(Number(pageFromUrl));
  }, []);

  const {
    data: withdraws,
    isLoading,
    isFetching,
  } = useGetWithdrawsQuery({ page: pageFromUrl });

  return (
    <>
    <div className="bg-black flex flex-col gap-4 px-4 lg:px-10 py-10">
      <div className="flex justify-between">
        <h3 className="text-primary text-xl font-medium">Recent Withdraws</h3>

        <div className="hidden md:flex items-center gap-4">
          <Link to="/app/giftcard">
            <button className="pb-2 border-b border-primary text-gray-300 text-sm">
              Sell Now
            </button>
          </Link>
        </div>
      </div>

      <div className="overflow-x-auto relative">
        {isLoading || isFetching ? (
          <WithdrawsTableSkeleton />
        ) : withdraws?.data.length == 0 ? (
          <div className="w-full bg-red font-light text-white/40 my-24 text-center">
            No withdraws found
          </div>
        ) : (
          <table className="w-full text-sm text-left text-gray-400">
            <thead className="text-xs uppercase bg-gray-700 text-gray-300">
              <tr>
                <th scope="col" className="py-3 px-6">
                  Transaction ID
                </th>
                <th scope="col" className="py-3 px-6">
                  Type
                </th>
                <th scope="col" className="py-3 px-6">
                  Withdrawal Type
                </th>
                <th scope="col" className="py-3 px-6">
                  Withdrawal Details
                </th>
                <th scope="col" className="py-3 px-6">
                  Source
                </th>
                <th scope="col" className="py-3 px-6">
                  Amount
                </th>
                <th scope="col" className="py-3 px-6">
                  Status
                </th>
                <th scope="col" className="py-3 px-6"></th>
              </tr>
            </thead>
            <tbody>
              {withdraws?.data?.map((data: any) => (
                <tr className="border-b bg-transparent border-gray-700">
                  <th
                    scope="row"
                    className="py-4 px-6 font-medium whitespace-nowrap text-white"
                  >
                    {data?._id}
                  </th>
                  <td className="py-4 px-6">{data?.type}</td>
                  <td className="py-4 px-6">
                    {data?.withdrawalType || "direct"}
                  </td>
                  <td className="py-4 px-6">
                    {data?.withdrawalType ===
                      (WithdrawalType.DirectWithdrawal ||
                        WithdrawalType.QuickCash) || !data?.withdrawalType ? (
                      <div>
                        <p>{data?.bank?.bankName}</p>
                        <p>{data?.bank?.accountNumber}</p>
                      </div>
                    ) : data?.withdrawalType === WithdrawalType.QuickCrypto ? (
                      <div>
                        <p>{data?.coin}</p>
                        <p>{data?.withdrawalDetails}</p>
                      </div>
                    ) : (
                      <div>
                        <p>{data?.withdrawalDetails}</p>
                      </div>
                    )}
                  </td>
                  <td className="py-4 px-6 uppercase">
                    {data?.source || "Account"}
                  </td>
                  <td className="py-4 px-6">{data?.amount}</td>
                  <td className="py-4 px-6">
                    {data.status === "completed" ? (
                      <div className="badge-success px-4 py-2 rounded-full">
                        Completed
                      </div>
                    ) : data.status === "pending" ? (
                      <div className="badge-warning px-4 py-2 rounded-full">
                        Pending
                      </div>
                    ) : data.status === "suspended" ? (
                      <div className="badge-suspended px-4 py-2 rounded-full">
                        Suspended
                      </div>
                    ) : data.status === "processing" ? (
                      <div className="badge-info px-4 py-2 rounded-full">
                        Processing
                      </div>
                    ) : data.status === "processed" ? (
                      <div className="badge-info px-4 py-2 rounded-full">
                        Processed
                      </div>
                    ) : data.status === "under-review" ? (
                      <div className="badge-info px-4 py-2 rounded-full">
                        Under Review
                      </div>
                    ) : (
                      <div className="badge-error px-4 py-2 rounded-full">
                        Cancelled
                      </div>
                    )}
                  </td>
                  <td className="py-4 px-6">
                    <Link to={`/app/transaction/withdraws/${data._id}`}>
                      <button className="box-content text-blue-500 text-xs">
                        Details
                      </button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="flex items-center gap-5 mt-12 ml-6">
          <p className="text-white/60 font-light text-sm">
            page: {pageFromUrl} | total: {withdraws?.total} withdraws
          </p>
          <div className="flex items-center gap-2">
            <Link
              to={`/app/transaction/withdraws?page=${
                pageFromUrl == "1" ? 1 : page - 1
              }`}
            >
              <button
                className="rounded-full bg-gray-500 hover:bg-gray-600 text-white/70 disabled:cursor-not-allowed disabled:bg-gray-500/50 disabled:text-white/20 text-lg cursor-pointer"
                disabled={pageFromUrl === "1"}
                onClick={() => {
                  if (page > 1) {
                    setPage(page - 1);
                  }
                }}
              >
                <FiChevronLeft className="box-content p-3" />
              </button>
            </Link>

            <Link
              to={`/app/transaction/withdraws?page=${
                page < withdraws?.last_page ? page + 1 : page
              }`}
            >
              <button
                className="rounded-full bg-gray-500 hover:bg-gray-600 text-white/70 disabled:cursor-not-allowed disabled:bg-gray-500/50 disabled:text-white/20 text-lg cursor-pointer"
                disabled={pageFromUrl == withdraws?.last_page}
                onClick={() => setPage(page + 1)}
              >
                <FiChevronRight className="box-content p-3" />
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
    <CryptoPayoutsTablePanel title="Recent Crypto Payouts" />
    </>
  );
};
